import { submitFormJson } from 'widgets/toolbox/ajax';
import { TBasicInputInstance } from 'widgets/forms/BasicInput';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';

/**
 * @description GiftCertificateForm widget factory
 * @param AjaxForm Base form class to extend
 */
function GiftCertificateFormClassCreator(AjaxForm: import('widgets/forms/AjaxForm').TAjaxForm) {
    /**
     * @category widgets
     * @subcategory forms
     * @class GiftCertificateForm
     * @augments AjaxForm
     * @classdesc Handles gift certificate apply/remove logic during checkout
     */
    class GiftCertificateForm extends AjaxForm {
        appliedCode: string = '';

        prefs() {
            return {
                successMsg: 'giftCertificateSuccessMsg',
                deletionMsg: 'giftCertificateRemoveMsg',
                applyUrl: '/Cart-RedeemGiftCertificate',
                removeGiftcertificateUrl: '',
                ...super.prefs()
            };
        }

        init() {
            super.init();

            const formEl = this.ref('self').get() as HTMLFormElement | null;

            if (formEl) {
                formEl.addEventListener('submit', e => {
                    e.preventDefault();
                    this.busy();

                    const formData = new FormData(formEl);
                    const formObj: { [key: string]: string } = {};

                    formData.forEach((value, key) => {
                        formObj[key] = value.toString();
                    });

                    submitFormJson(this.prefs().applyUrl, formObj)
                        .then(this.onSubmitted.bind(this))
                        .finally(() => this.unbusy());
                });
            }

            this.eventBus().on('show.gc.applied.message', 'onShowGiftCertificateApplied');
        }

        /**
         * @description Handles successful form submission
         */
        onSubmitted(data: TServerResponse) {
            if (!data) {
                return;
            }

            this.appliedCode = typeof data.code === 'string' ? data.code : '';

            this.has(this.prefs().successMsg, msg => msg.setText('').hide());
            this.has(this.prefs().deletionMsg, msg => msg.setText('').hide());
            this.ref('errorFeedback').setText('').hide(); // reset

            if (!data.success && data.errorMessage) {
                this.ref('errorFeedback').setText(data.errorMessage).show();
            } else if (!data.success && data.fieldErrors) {
                Object.entries(data.fieldErrors).forEach(([field, msg]) => {
                    this.getById(field, (input: TBasicInputInstance) => input.setError(msg));
                });
            } else {
                this.ref('errorFeedback').setText('').hide();
                this.updateInfoMessage(data);
                this.eventBus().emit('giftcertificate.updated', data);
                this.eachField(field => {
                    if (field.initValue) {
                        field.setValue('', false);
                    }

                    field.clearError();
                });
            }
        }

        /**
         * @description Removes an applied gift certificate
         * @param button Ref to the remove button
         */
        removeGiftCertificate(button: TRefElementInstance) {
            this.busy();

            this.has(this.prefs().deletionMsg, msg => msg.setText('').hide());
            this.has(this.prefs().successMsg, msg => msg.setText('').hide());

            const uuid = button.data<string>('code');

            return submitFormJson(this.prefs().removeGiftcertificateUrl, {
                uuid: uuid
            })
                .then(response => {
                    if (!response.success && response.errorMessage) {
                        this.has(this.prefs().deletionMsg, msg => msg.setText(response.errorMessage).show());

                        return response;
                    }

                    this.eachField(field => {
                        if (field.initValue) {
                            field.setValue('', false);
                        }

                        field.clearError();
                    });
                    this.ref('errorFeedback').setText('').hide();

                    response.code = uuid;
                    response.gcAction = 'removed';
                    this.eventBus().emit('giftcertificate.updated', response);
                    this.updateInfoMessage(response);

                    return response;
                })
                .finally(() => {
                    this.unbusy();
                });
        }

        updateInfoMessage(data: TServerResponse) {
            requestAnimationFrame(() => {
                this.has('infoMessageLabel', infoRef => {
                    const infoMessageObj = data.infoMessageObj as { fullCovered: string; partiallyCovered: string };
                    const totals = data?.totals as {
                        giftCertificateTotalPrice?: {
                            value: number;
                            formatted: string;
                        };
                        remainingAfterGiftCertificates?: {
                            value: number;
                            formatted: string;
                        };
                    };

                    const covered = totals?.giftCertificateTotalPrice?.value;
                    const remaining = totals?.remainingAfterGiftCertificates?.value;

                    if (!infoMessageObj || typeof covered !== 'number' || typeof remaining !== 'number') {
                        infoRef.setText('').hide();

                        return;
                    }

                    if (covered > 0 && remaining <= 0) {
                        infoRef.setText(infoMessageObj.fullCovered).removeClass('b-message--error').addClass('m-success').show();
                    } else if (covered > 0 && remaining > 0) {
                        infoRef.setText(infoMessageObj.partiallyCovered).removeClass('b-message--error m-success').show();
                    } else {
                        infoRef.setText('').hide();
                    }
                });
            });
        }
    }

    return GiftCertificateForm;
}

export type TGiftCertificateForm = ReturnType<typeof GiftCertificateFormClassCreator>;
export type TGiftCertificateFormInstance = InstanceType<TGiftCertificateForm>;

export default GiftCertificateFormClassCreator;
